import { forwardRef } from 'react';
import { AppointmentCard } from '../../../AppointmentCard/AppointmentCard';
import { GenericClickable } from '../../../GenericClickable/GenericClickable';
import css from './SearchGridAppointmentTile.module.scss';
import { SearchGridAppointmentTileProps } from './SearchGridAppointmentTile.types';
import { GMGrid } from '../../../Grid';
import { AppointmentLoanerOrRentalOption, AppointmentType } from '../../../../API/Enums';
import { AppointmentColors } from '../../../../components/Admin/KeyManagementRoutes/CheckIns/Filters/Filters.types';
import { GoMotoFunction } from '../../../../API/GoMoto.types';
import { AppointmentCardProps } from '../../../AppointmentCard/AppointmentCard.types';

/**
 * A wrapper component for the {@link AppointmentCard} control so it can be clickable and display correctly
 * in the `SearchGrid` control.
 *
 * @param tabIndex - {@link SearchGridAppointmentTileProps.tabIndex tabIndex} description.
 * @param title - {@link SearchGridAppointmentTileProps.title title} description.
 * @param onClick - {@link SearchGridAppointmentTileProps.onClick onClick} description.
 * @param appointment - {@link SearchGridAppointmentTileProps.appointment appointment} description.
 * @param description - {@link SearchGridAppointmentTileProps.description description} description.
 * @param onAddKeyClick - {@link SearchGridAppointmentTileProps.onAddKeyClick onAddKeyClick} description.
 * @param appointmentTileType - {@link SearchGridAppointmentTileProps.appointmentTileType appointmentTileType} description.
 * @param appointmentTileColorType - {@link SearchGridAppointmentTileProps.tabType tabType} description.
 */
export const SearchGridAppointmentTile = forwardRef<HTMLDivElement, SearchGridAppointmentTileProps>(({
    tabIndex,
    title,
    onClick,
    appointment,
    description,
    onAddKeyClick,
    appointmentTileType,
    tabType,
}, ref) => {
    /**
     * Gets the color that should be used for the appointment card based on what tab
     * is being displayed and the type of appointment.
     */
    const getColor: GoMotoFunction<void, AppointmentCardProps['color']> = () => {
        if (tabType === 'check-ins') {
            if (appointmentTileType === 'priority') {
                return AppointmentColors[AppointmentType.priority];
            }
            else if (appointmentTileType === 'dms') {
                return AppointmentColors[AppointmentType.dmsAppointment];
            }
            else if (!appointment.adminCreated && !appointment.keySlot) {
                return AppointmentColors[AppointmentType.checkedIn];
            }
            else if (appointment.adminCreated) {
                return AppointmentColors[AppointmentType.created];
            }
            else {
                return AppointmentColors[AppointmentType.none];
            }
        }
        else if (tabType === 'loaded-keys') {
            if ((appointment.keyStatus === 'slot_assigned' || appointment.keyStatus === 'reopen requested dropoff') && appointment.storedByAdvisor === false) {
                return AppointmentColors[AppointmentType.keyDroppedOff];
            }
            else if (
                appointment.keySlot
                && appointment.storedByAdvisor === true
                && !(appointment.keyStatus === 'loaner key stored' || appointment.keyStatus === 'rental key stored' || appointment.keyStatus === 'reopen requested exchange')
            ) {
                return AppointmentColors[AppointmentType.inKeyBin];
            }
            else if (
                appointment.returningLoanerOrRental !== null
                && appointment.returningLoanerOrRental !== AppointmentLoanerOrRentalOption.None
                && (appointment.keyStatus === 'loaner key stored' || appointment.keyStatus === 'rental key stored' || appointment.keyStatus === 'reopen requested exchange')
            ) {
                return AppointmentColors[AppointmentType.rentalOrLoanerInBin];
            }
            else {
                return AppointmentColors[AppointmentType.none];
            }
        }
        else {
            return AppointmentColors[AppointmentType.none];
        }
    };

    return (
        <GMGrid
            container
            xs={4}
            ref={ref}
            className={css.resultItem}
        >
            <GenericClickable
                className={css.genericClickable}
                tabIndex={tabIndex}
                title={title}
                onClick={onClick}
            >
                <AppointmentCard
                    appointment={appointment}
                    onAddKeyClick={onAddKeyClick}
                    color={getColor()}
                    description={description}
                />
            </GenericClickable>
        </GMGrid>
    );
});